import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Icon from '../../../components/AppIcon';

const SourceEffectivenessChart = () => {
  const [viewMode, setViewMode] = useState('volume'); // 'volume' or 'quality'

  const sourceData = [
    { 
      name: 'LinkedIn', 
      applications: 987, 
      hires: 27, 
      percentage: 35, 
      color: '#0077B5',
      costPerHire: 3200,
      timeToHire: 28,
      qualityScore: 7.8
    },
    { 
      name: 'Indeed', 
      applications: 789, 
      hires: 22, 
      percentage: 28, 
      color: '#2557A7',
      costPerHire: 2800,
      timeToHire: 32,
      qualityScore: 6.5
    },
    { 
      name: 'Company Website', 
      applications: 507, 
      hires: 14, 
      percentage: 18, 
      color: '#059669',
      costPerHire: 1900,
      timeToHire: 25,
      qualityScore: 8.9
    },
    { 
      name: 'Employee Referrals', 
      applications: 338, 
      hires: 19, 
      percentage: 12, 
      color: '#DC2626',
      costPerHire: 1200,
      timeToHire: 18,
      qualityScore: 9.2
    },
    { 
      name: 'Glassdoor', 
      applications: 197, 
      hires: 6, 
      percentage: 7, 
      color: '#0CAE00',
      costPerHire: 3800,
      timeToHire: 35,
      qualityScore: 6.8
    }
  ];

  // Calculate conversion rates and quality metrics
  const enhancedData = sourceData?.map(source => ({
    ...source,
    conversionRate: ((source?.hires / source?.applications) * 100)?.toFixed(1),
    efficiencyScore: (source?.qualityScore * (source?.hires / source?.applications * 100) / (source?.costPerHire / 1000))?.toFixed(1)
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-white/95 backdrop-blur-sm border border-slate-200 rounded-lg p-4 shadow-lg min-w-52">
          <div className="flex items-center gap-2 mb-3">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: data?.color }}
            />
            <h4 className="font-semibold text-slate-900">{data?.name}</h4>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Applications:</span>
              <span className="font-semibold">{data?.applications?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Successful Hires:</span>
              <span className="font-semibold text-emerald-600">{data?.hires}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Conversion Rate:</span>
              <span className="font-semibold">{data?.conversionRate}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Avg. Cost:</span>
              <span className="font-semibold">${data?.costPerHire?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Quality Score:</span>
              <span className="font-semibold">{data?.qualityScore}/10</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return percent > 0.05 ? (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="600"
      >
        {`${(percent * 100)?.toFixed(0)}%`}
      </text>
    ) : null;
  };

  const bestPerformer = enhancedData?.reduce((best, current) => 
    parseFloat(current?.efficiencyScore) > parseFloat(best?.efficiencyScore) ? current : best
  );

  const totalApplications = sourceData?.reduce((sum, source) => sum + source?.applications, 0);
  const totalHires = sourceData?.reduce((sum, source) => sum + source?.hires, 0);
  const overallConversion = ((totalHires / totalApplications) * 100)?.toFixed(1);

  return (
    <div className="space-y-6">
      {/* Header with View Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">Source Effectiveness</h3>
          <p className="text-sm text-slate-500">Application sources by volume and quality</p>
        </div>
        
        <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-lg">
          <button
            onClick={() => setViewMode('volume')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'volume' ?'bg-white text-slate-900 shadow-sm' :'text-slate-600 hover:text-slate-900'
            }`}
          >
            Volume View
          </button>
          <button
            onClick={() => setViewMode('quality')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'quality' ?'bg-white text-slate-900 shadow-sm' :'text-slate-600 hover:text-slate-900'
            }`}
          >
            Quality View
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart Section */}
        <div className="h-80">
          {viewMode === 'volume' ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={enhancedData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={100}
                  innerRadius={40}
                  fill="#8884d8"
                  dataKey="applications"
                  paddingAngle={2}
                >
                  {enhancedData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={enhancedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="conversionRate" radius={[4, 4, 0, 0]}>
                  {enhancedData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Metrics Section */}
        <div className="space-y-4">
          {/* Overall Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-slate-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-slate-900">{totalApplications?.toLocaleString()}</div>
              <div className="text-xs text-slate-500">Total Applications</div>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-emerald-600">{overallConversion}%</div>
              <div className="text-xs text-emerald-700">Avg. Conversion</div>
            </div>
          </div>

          {/* Source Performance List */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-slate-700 mb-3">Source Performance Ranking</h4>
            {enhancedData
              ?.sort((a, b) => parseFloat(b?.efficiencyScore) - parseFloat(a?.efficiencyScore))
              ?.map((source, index) => (
                <div key={source?.name} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-500 w-4">#{index + 1}</span>
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: source?.color }}
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">{source?.name}</div>
                      <div className="text-xs text-slate-500">{source?.conversionRate}% conversion</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-900">{source?.hires} hires</div>
                    <div className="text-xs text-slate-500">Score: {source?.efficiencyScore}</div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Best Performer Highlight */}
      <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Icon name="Award" size={18} className="text-emerald-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-emerald-900 mb-2">🏆 Top Performing Source</h4>
            <div className="text-sm text-emerald-800">
              <p className="mb-1">
                <strong>{bestPerformer?.name}</strong> leads with {bestPerformer?.conversionRate}% conversion rate and 
                ${bestPerformer?.costPerHire?.toLocaleString()} cost per hire.
              </p>
              <p className="text-emerald-700">
                Quality Score: {bestPerformer?.qualityScore}/10 • Avg. Time: {bestPerformer?.timeToHire} days
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SourceEffectivenessChart;